import { locate } from "./targets";
import type { Anchor, Moment, Trigger, TriggerScope } from "./types";

/**
 * Triggers are presence edges, not product state: the shell's own contexts
 * are several and some are unobservable from outside, so the layer reads the
 * DOM through the same visibility the ring uses. An edge is a change in an
 * anchor's presence between two ticks, where occluded counts as present: a
 * modal opening or closing over the shell is never an edge.
 */
export type Presence = Readonly<Record<Anchor, boolean>>;

export interface Survey {
  now: Presence;
  /** Present or occluded. */
  visible: ReadonlySet<Anchor>;
  /** The subset under an open modal, menu or select. */
  occluded: ReadonlySet<Anchor>;
  /** The subset that says `aria-expanded="false"`. */
  collapsed: ReadonlySet<Anchor>;
}

/** One read of the DOM for every anchor. */
export function survey(anchors: readonly Anchor[]): Survey {
  const now: Record<Anchor, boolean> = {};
  const visible = new Set<Anchor>();
  const occluded = new Set<Anchor>();
  const collapsed = new Set<Anchor>();
  for (const a of anchors) {
    const found = locate(a);
    now[a] = found !== null;
    if (!found) continue;
    visible.add(a);
    if (found.presence === "occluded") occluded.add(a);
    if (found.el.getAttribute("aria-expanded") === "false") collapsed.add(a);
  }
  return { now, visible, occluded, collapsed };
}

export interface Edge {
  kind: "appears" | "disappears";
  target: Anchor;
}

export interface TrackedAnchor {
  target: Anchor;
  scope: TriggerScope;
}

/** Every anchor a trigger names, with its scope (`shell` by default). */
export function triggerAnchors(moments: readonly Moment[]): TrackedAnchor[] {
  const seen = new Map<Anchor, TrackedAnchor>();
  for (const m of moments) {
    if (!m.when || seen.has(m.when.target)) continue;
    seen.set(m.when.target, {
      target: m.when.target,
      scope: m.when.scope ?? "shell",
    });
  }
  return [...seen.values()];
}

/** Every anchor the registry names: card targets, triggers, beats, clears. */
export function allAnchors(moments: readonly Moment[]): Anchor[] {
  const names = new Set<Anchor>();
  for (const m of moments) {
    if (m.when) names.add(m.when.target);
    for (const c of m.cards) {
      names.add(c.target);
      if (c.clear) names.add(c.clear);
      if (c.before) names.add(c.before.target);
    }
  }
  return [...names];
}

/** An anchor never measured reads as absent, so what is on screen at activation has appeared. */
export function diffPresence(
  prev: Presence,
  now: Presence,
  anchors: readonly Anchor[],
): Edge[] {
  const edges: Edge[] = [];
  for (const target of anchors) {
    const was = prev[target] ?? false;
    const is = now[target] ?? false;
    if (was === is) continue;
    edges.push({ kind: is ? "appears" : "disappears", target });
  }
  return edges;
}

/** Forget these anchors, so their next sighting is a fresh appearance. */
export function resetScope(
  prev: Presence,
  anchors: readonly Anchor[],
): Presence {
  if (anchors.length === 0) return prev;
  const next = { ...prev };
  for (const target of anchors) next[target] = false;
  return next;
}

export function fired(trigger: Trigger, edges: readonly Edge[]): boolean {
  return edges.some(
    (e) => e.kind === trigger.kind && e.target === trigger.target,
  );
}
